import { Tooltip } from '@mui/material';
import { AxiosError } from 'axios';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  Card,
  Divider,
  Icons,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import { StorageAction, StorageActionsSummary } from '@cast/types-stop';

import { ActivityFeed } from './ActivityFeed';
import { AutonomousActionsCount } from './AutonomousActionsCount';
import { RiskStatCells } from './RiskStatCells';
import { useAllActivityDrawer } from '../AllActivityDrawer';

import { FailedToLoad } from '@cast/shared-messages-ui';

type CardContentProps = {
  storageActions: StorageAction[];
  storageActionsSummary: StorageActionsSummary;
  isLoading: boolean;
  totalCount: number;
};

const CardContent = ({
  storageActions,
  storageActionsSummary,
  isLoading,
  totalCount,
}: CardContentProps) => (
  <Box sx={{ display: 'flex' }}>
    {/* Left — autonomous actions count */}
    <AutonomousActionsCount totalCount={totalCount} isLoading={isLoading} />

    <Divider orientation="vertical" flexItem />

    {/* Middle — risk stat cells */}
    <RiskStatCells
      storageActionsSummary={storageActionsSummary}
      isLoading={isLoading}
    />

    <Divider orientation="vertical" flexItem />

    {/* Right — activity feed */}
    <ActivityFeed storageActions={storageActions} isLoading={isLoading} />
  </Box>
);

type OptimizationActionsSectionProps = {
  storageActions: StorageAction[];
  storageActionsSummary: StorageActionsSummary;
  isLoading: boolean;
  totalCount: number;
  error: AxiosError | null;
  refetch: () => void;
  nodeName?: string;
};

export const OptimizationActionsSection = ({
  storageActions,
  storageActionsSummary,
  isLoading,
  totalCount,
  error,
  refetch,
  nodeName,
}: OptimizationActionsSectionProps) => {
  const { openAllActivity } = useAllActivityDrawer(nodeName);

  return (
    <Box data-testid="optimization-actions-section">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 8 }}
      >
        <Stack direction="row" gap={6}>
          <Typography variant="h6">Optimization actions</Typography>
          <Tooltip
            title={`Autonomous changes ${CAST_AI} made to disk configuration on this node.`}
            arrow
            placement="top"
          >
            <Box
              component="span"
              sx={{ display: 'inline-flex', cursor: 'help', color: 'grey.700' }}
            >
              <Icons.Info size={12} />
            </Box>
          </Tooltip>
        </Stack>
        {storageActions.length > 0 && (
          <Button
            testId="view-all-activity-button"
            variant="text"
            size="md"
            disabled={isLoading}
            onClick={openAllActivity}
          >
            View all
          </Button>
        )}
      </Stack>
      <Card
        sx={{
          p: 0,
          overflow: 'hidden',
          minHeight: '242px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error ? (
          <FailedToLoad
            title={error.message}
            sx={{ flex: 1, justifyContent: 'center' }}
            refresh={refetch}
          />
        ) : (
          <CardContent
            storageActions={storageActions}
            storageActionsSummary={storageActionsSummary}
            isLoading={isLoading}
            totalCount={totalCount}
          />
        )}
      </Card>
    </Box>
  );
};
